import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Timeline as MovementsIcon,
} from '@mui/icons-material';

/**
 * REPORTING-CENTER.1 — help modal for the Inventory Movements & Stock on Hand
 * report, following the InventoryHelpModal.tsx idiom. Covers the four
 * semantics every reader of this report must know: ledger-sum opening,
 * postedAt dates, current-cost valuation, and the location-filter caveat.
 */

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const InventoryMovementsReportHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark ? 'rgba(22, 22, 35, 0.90)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MovementsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Movements &amp; Stock on Hand Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              How the numbers are computed
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          For every item this report shows: <strong>Opening</strong> (stock at the start of your
          range), the period&apos;s movements grouped into columns (received, sales, waste, transfers,
          adjustments), and <strong>Closing</strong> = opening + net change. It is computed from the
          movement ledger only — no stock count is involved.
        </Typography>

        <Divider />

        <Note>
          <strong>Opening is a ledger sum.</strong> It adds up every movement recorded before your
          start date — it is not taken from a stock count. If historical movements are missing
          (e.g. stock existed before the venue started using inventory), opening will be
          correspondingly low; a posted count&apos;s variance movement corrects the ledger from that
          point on.
        </Note>

        <Note variant="warning">
          <strong>Dates are entry time (postedAt), not business dates.</strong> A movement counts in
          the period when it was <em>recorded</em>. Imported sales are posted at import time, so a
          January sales file imported in March lands in March on this report.
        </Note>

        <Note variant="warning">
          <strong>How values are calculated.</strong> Once cost tracking is active,{' '}
        <strong>Closing Value</strong> is the sum of each remaining batch at its own recorded cost —
        the same basis as the Stock Value page, so the two agree. <strong>Opening Value</strong> is
        quantity × the item&apos;s current last cost, an estimate for the period start. Before cost
        tracking is active, both use quantity × current last cost. Items with no recorded cost fall
        back to the last known cost. The value column is hidden for users without stock-cost access.
      </Note>

        <Note>
          <strong>Location filter caveat.</strong> When you filter by location, movements recorded
          without a location are not included. Venue-wide (no location filter) always includes
          every movement.
        </Note>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryMovementsReportHelpModal;
